import { FC, useEffect, useLayoutEffect, useRef } from 'react';

import { useRouter } from 'next/router';

import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { showSideMenuAtom } from '@recoil_iqfu/atoms/side_menu/showSideMenu';
import { sideMenuScrollPositionAtomFamily } from '@recoil_iqfu/atoms/side_menu/sideMenuScrollPosition';
import { spMenuOpenAtom } from '@recoil_iqfu/atoms/side_menu/spMenuOpen';

import AdminSideMenu from '@components_common/side_menu/AdminSideMenu';
import CompanySideMenu from '@components_common/side_menu/CompanySideMenu';
import FinancialAgencySideMenu from '@components_common/side_menu/FinancialAgencySideMenu';
import { UserType } from '@constants_common';
import { useAuth } from '@contexts_common/authContext';

import styles from './PersistentSideMenu.module.scss';

const SIDE_MENU_USER_TYPES: UserType[] = ['admin', 'company', 'financial_agency'];
const AUTH_PAGE_SUFFIXES = ['/login', '/forgot-password', '/reset-password'];

const isAuthPage = (pathname: string): boolean => AUTH_PAGE_SUFFIXES.some((suffix) => pathname.endsWith(suffix));

const PersistentSideMenu: FC = () => {
  const { userType } = useAuth();
  const router = useRouter();

  if (!SIDE_MENU_USER_TYPES.includes(userType)) return null;
  if (!router.pathname.startsWith('/console/')) return null;
  if (isAuthPage(router.pathname)) return null;

  return <PersistentSideMenuInner key={userType} userType={userType} />;
};

const PersistentSideMenuInner: FC<{ userType: UserType }> = ({ userType }) => {
  const router = useRouter();
  const showSideMenu = useRecoilValue(showSideMenuAtom);
  const [spMenuOpen, setSpMenuOpen] = useRecoilState(spMenuOpenAtom);
  const toggleSpMenu = () => setSpMenuOpen(!spMenuOpen);

  const sideMenuRef = useRef<HTMLDivElement>(null);
  const setSideMenuScrollPosition = useSetRecoilState(sideMenuScrollPositionAtomFamily(userType));
  const restoreSideMenuScrollPosition = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        if (!sideMenuRef.current) return;
        const position = snapshot.getLoadable(sideMenuScrollPositionAtomFamily(userType)).getValue();
        sideMenuRef.current.scrollTo(0, position);
      },
    [userType],
  );

  useEffect(() => {
    const flushScrollPosition = () => {
      if (sideMenuRef.current) {
        setSideMenuScrollPosition(sideMenuRef.current.scrollTop);
      }
    };
    const handleRouteChange = () => {
      flushScrollPosition();
      setSpMenuOpen(false);
    };
    router.events.on('routeChangeStart', handleRouteChange);
    window.addEventListener('pagehide', flushScrollPosition);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('pagehide', flushScrollPosition);
    };
  }, [router.events, setSpMenuOpen, setSideMenuScrollPosition]);

  useLayoutEffect(() => {
    const container = sideMenuRef.current;
    if (!container) return;

    let userScrolled = false;
    restoreSideMenuScrollPosition();

    const resizeObserver = new ResizeObserver(() => {
      if (userScrolled) return;
      restoreSideMenuScrollPosition();
    });
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild);
    }

    const handleUserInteract = () => {
      userScrolled = true;
      resizeObserver.disconnect();
    };
    container.addEventListener('wheel', handleUserInteract, { passive: true });
    container.addEventListener('touchstart', handleUserInteract, { passive: true });
    container.addEventListener('keydown', handleUserInteract);
    container.addEventListener('mousedown', handleUserInteract);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('wheel', handleUserInteract);
      container.removeEventListener('touchstart', handleUserInteract);
      container.removeEventListener('keydown', handleUserInteract);
      container.removeEventListener('mousedown', handleUserInteract);
    };
  }, [restoreSideMenuScrollPosition]);

  return (
    <div
      ref={sideMenuRef}
      className={`${styles.side_menu} ${userType === 'admin' && !showSideMenu ? styles.hides_side_menu : ''}`}
    >
      {userType === 'company' && <CompanySideMenu toggleSpMenu={toggleSpMenu} spMenuOpen={spMenuOpen} />}
      {userType === 'admin' && <AdminSideMenu toggleSpMenu={toggleSpMenu} spMenuOpen={spMenuOpen} />}
      {userType === 'financial_agency' && (
        <FinancialAgencySideMenu toggleSpMenu={toggleSpMenu} spMenuOpen={spMenuOpen} />
      )}
    </div>
  );
};

export default PersistentSideMenu;
